import React, { Component } from 'react';
import autobind from 'class-autobind';
import classNames from 'classnames';

import map from 'lodash/map';
import each from 'lodash/each';
import invoke from 'lodash/invoke';
import compact from 'lodash/compact';
import findIndex from 'lodash/findIndex';

import { 
  setTextLinksToDisplayExternalOpenMsg
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  ArrowCircleLeftIcon,
  ArrowCircleRightIcon
} from 'zoomforth_component_lib/external_modern_icons.jsx';

import {
  ConditionalSkipLinkForElement
} from 'zoomforth_codesbane_viewer/component_lib/skip_link_for_element.jsx';

import {
  getInternalLinkUrl
} from 'zoomforth_codesbane_viewer/lib/app_actions.js';

import {
  isVideoish
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  VisuallyHiddenSpan
} from 'zoomforth_codesbane_viewer/component_lib/visually_hidden.jsx';

import Carousel from 'zoomforth_codesbane_viewer/component_lib/carousel.jsx';

// because we pass the current slideshow slide to the URL we cannot rely on
// simply tracking the current index. we need to determine the actual index
// based on the selected content item which was reported to the Redux store
const getSelectedSlideIndex = (contentItemAssetPairs, selectedContentItem)=>
  selectedContentItem ? findIndex(contentItemAssetPairs, ['contentItem.uuid', selectedContentItem.uuid]) : 0;


const SelectSlideBtn = ({ accessibleText, subpageId, contentItem, btnContent, className, onSlideSelect, onSelectContentClick })=> (
  <a
    className={classNames('accessible-slideshow-btn', className)}
    href={getInternalLinkUrl({ subpageId, contentUuid: contentItem.uuid })}
    onClick={(e)=> {
      e.preventDefault();
      onSlideSelect(contentItem);
      if (onSelectContentClick) {
        onSelectContentClick(contentItem, { shouldSyncUrl: false });
      }
    }}
  >
    { btnContent }

    <VisuallyHiddenSpan>
      { accessibleText }
    </VisuallyHiddenSpan>
  </a>
);


class SlideshowBase_ViewerVersion2 extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isVideoPlaying: false,
    };

    this.slideshowRef = null;

    autobind(this);
  }

  componentWillMount() {
    // preload images
    each(compact(this.props.assetUrlsForPreload), (assetUrl) => {
      (new Image()).src = assetUrl;
    });
  }

  handleSlideshowRefReceived(ref) {
    this.slideshowRef = ref;

    setTextLinksToDisplayExternalOpenMsg(this.slideshowRef, '.accessible-slideshow-slide-info a');
  }

  handleSetVideoIsPlaying(result) {
    this.setState({ isVideoPlaying: result });
  }

  render() {
    const {
      isVideoPlaying
    } = this.state;

    const {
      SlideshowItemComponent,
      height,
      backgroundStyle,
      sectionId, 
      subpageId,
      contentItemAssetPairs,
      selectedContentItem, 
      videoishPlayerProps,
      SlideshowThumbnailComponent,
      onSlideDisplay,
      onSlideSelect,
      onSelectContentClick
    } = this.props;
    
    // Early return in case we have nothing to render
    if (contentItemAssetPairs.length === 0) {
      return <div className="section-wrapper section-type-slideshow" />;
    }

    const shouldShowCaption = selectedContentItem && (
      (selectedContentItem.caption && selectedContentItem.caption.length > 0) ||
      (selectedContentItem.title  && selectedContentItem.title.length > 0)
    );

    const currentSlideIdx = getSelectedSlideIndex(contentItemAssetPairs, selectedContentItem);

    const atFirstSlide = currentSlideIdx === 0;
    const atLastSlide = currentSlideIdx === (contentItemAssetPairs.length - 1);

    const { contentItem: nextContentItem } = (
      (atLastSlide || currentSlideIdx + 1 > contentItemAssetPairs.length) ?
        {}
      :
        contentItemAssetPairs[currentSlideIdx + 1]
    );

    const { contentItem: previousContentItem } = (
      (atFirstSlide || currentSlideIdx - 1 < 0) ?
        {}
      :
        contentItemAssetPairs[currentSlideIdx - 1]
    );

    return (
      <ConditionalSkipLinkForElement
        skipLinkText="Skip slideshow"
        shouldShowSkipLink={true}
      >
        <div
          style={{ ...backgroundStyle, height }}
          ref={this.handleSlideshowRefReceived}
          data-scroll-to-section-id={sectionId}
          className={classNames('accessible-slideshow-wrapper', {
            'at-first-index': atFirstSlide,
            'at-last-index': atLastSlide,
            'has-caption': shouldShowCaption,
            'video-playing': isVideoPlaying
          })
        }>
          <div className="thumbnails" tabIndex="-1">
            {
              map(contentItemAssetPairs, ({ contentItem, asset }, idx) => (
                <SlideshowThumbnailComponent
                  key={contentItem.eid || contentItem.uuid}
                  idx={idx}
                  uuid={contentItem.uuid}
                  isVideo={isVideoish(contentItem)}
                  isActive={currentSlideIdx === idx}
                  subpageId={subpageId}
                  contentItem={contentItem}
                  asset={asset}
                  onClick={()=> {
                    onSlideSelect(contentItem);
                  }}
                  onSelectContentClick={onSelectContentClick}
                />
              ))
            }
          </div>

          <Carousel 
            currentSlideIdx={currentSlideIdx}
            viewportClassName="accessible-slideshow-viewport"
            viewportProps={{
              'aria-label': 'Slideshow of images and videos'
            }}
          >
            {
              map(contentItemAssetPairs, ({ contentItem, asset }, idx) => (
                <div key={contentItem.eid || contentItem.uuid} id={`slide${idx+1}`}>
                  <SlideshowItemComponent
                    idx={idx}
                    uuid={contentItem.uuid}
                    contentItem={contentItem}
                    asset={asset}
                    currentSlideIdx={currentSlideIdx}
                    isPlayable={isVideoish(contentItem)}
                    isSelected={currentSlideIdx === idx}
                    onSetVideoPlaying={this.handleSetVideoIsPlaying}
                    onSlideDisplay={() => {
                      onSlideDisplay(contentItem);
                    }}
                    videoishPlayerProps={{
                      ...videoishPlayerProps,
                      onEnded: (videoEventData)=> { 
                        invoke(videoishPlayerProps, 'onEnded', contentItem, videoEventData); 
                      },
                      onError: (videoEventData)=> { 
                        invoke(videoishPlayerProps, 'onError', contentItem, videoEventData); 
                      },
                      onPause: (videoEventData)=> { 
                        invoke(videoishPlayerProps, 'onPause', contentItem, videoEventData); 
                      },
                      onPlay: (videoEventData)=> { 
                        invoke(videoishPlayerProps, 'onPlay', contentItem, videoEventData); 
                      },
                      onSeeked: (videoEventData)=> { 
                        invoke(videoishPlayerProps, 'onSeeked', contentItem, videoEventData); 
                      },
                      onStart: (videoEventData)=> { 
                        invoke(videoishPlayerProps, 'onStart', contentItem, videoEventData); 
                      },
                      onTimeUpdate: (videoEventData)=> { 
                        invoke(videoishPlayerProps, 'onTimeUpdate', contentItem, videoEventData); 
                      }
                    }}
                  />

                  {
                    (
                      (contentItem.title  && contentItem.title.length > 0) ||
                      (contentItem.caption && contentItem.caption.length > 0)
                    ) &&
                      <div className="accessible-slideshow-slide-info">
                        {
                          contentItem.title &&
                            <div
                              className="accessible-slideshow-slide-title"
                              dangerouslySetInnerHTML={{ __html: contentItem.title }}
                            />
                        }

                        {
                          contentItem.caption &&
                            <div
                              className="accessible-slideshow-slide-description"
                              dangerouslySetInnerHTML={{ __html: contentItem.caption }}
                            />
                        }
                      </div>
                  }
                </div>
              ))
            }
          </Carousel>

          {
            previousContentItem &&
              <div className="accessible-slideshow-nav accessible-prev-btn">
                <SelectSlideBtn 
                  btnContent={<ArrowCircleLeftIcon />}
                  accessibleText="View previous slide"
                  className="accessible-slideshow-nav-btn"
                  subpageId={subpageId}
                  contentItem={previousContentItem}
                  onSlideSelect={onSlideSelect}
                  onSelectContentClick={onSelectContentClick}
                />
              </div>
          }

          {
            nextContentItem &&
              <div className="accessible-slideshow-nav accessible-next-btn">
                <SelectSlideBtn 
                  btnContent={<ArrowCircleRightIcon />}
                  accessibleText="View next slide"
                  className="accessible-slideshow-nav-btn"
                  subpageId={subpageId}
                  contentItem={nextContentItem}
                  onSlideSelect={onSlideSelect}
                  onSelectContentClick={onSelectContentClick}
                />
              </div>
          }

        </div>
      </ConditionalSkipLinkForElement>
    );
  }

}

export default SlideshowBase_ViewerVersion2;
